import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';

interface SupplierMetric {
  label: string;
  value: string;
  trend: 'up' | 'down' | 'stable';
  change: string;
}

interface FAQ {
  question: string;
  answer: string;
}

interface Feature {
  icon: string;
  title: string;
  description: string;
}

interface Testimonial {
  quote: string;
  author: string;
  position: string;
  company: string;
}

@Component({
  selector: 'app-supplier-iq',
  standalone: true,
  imports: [CommonModule, RouterModule],
  template: `
    <!-- Hero Section -->
    <section class="relative bg-gradient-to-br from-gray-900 to-blue-900 text-white py-16 md:py-24">
      <div class="absolute inset-0 bg-pattern opacity-20"></div>
      <div class="container mx-auto px-4 relative z-10">
        <div class="flex flex-col md:flex-row items-center">
          <div class="md:w-1/2 mb-10 md:mb-0">
            <div class="inline-flex items-center space-x-2 px-4 py-1.5 rounded-full bg-white/15 backdrop-blur-sm text-sm font-medium mb-6">
              <span class="w-2 h-2 rounded-full bg-blue-400 animate-pulse"></span>
              <span>Supply Chain Intelligence</span>
            </div>
            <h1 class="text-3xl md:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Supplier IQ
            </h1>
            <p class="text-xl md:text-2xl mb-8 text-blue-100">
              Transform your supplier management with AI-powered insights, comprehensive risk assessment tools, and real-time monitoring of your entire supply chain ecosystem.
            </p>
            <div class="flex flex-col sm:flex-row gap-4">
              <a href="#contact" class="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Request a Demo
              </a>
              <a href="#features" class="bg-transparent hover:bg-white/10 border border-white text-white font-bold py-3 px-6 rounded-lg transition duration-300 text-center">
                Explore Features
              </a>
            </div>
          </div>
          <div class="md:w-1/2">
            <div class="bg-white/10 backdrop-blur-sm rounded-xl p-6 shadow-2xl">
              <img src="https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?auto=format&fit=crop&q=80&w=1000" 
                   alt="Supplier IQ Dashboard showing supplier performance metrics and risk assessment" class="rounded-lg w-full">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Key Features Section -->
    <section class="py-16 bg-gray-50" id="features">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Key Features</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Our Supplier IQ platform provides comprehensive tools to optimize your supplier relationships, minimize supply chain risks, and ensure business continuity through advanced analytics and predictive intelligence.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          @for (feature of features; track feature.title) {
            <div class="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition duration-300 border border-gray-100">
              <div class="text-blue-600 text-4xl mb-4">
                <i class="fas {{ feature.icon }}"></i>
              </div>
              <h3 class="text-xl font-bold mb-3 text-gray-900">{{ feature.title }}</h3>
              <p class="text-gray-600">{{ feature.description }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- Risk Assessment Section -->
    <section class="py-16 bg-white">
      <div class="container mx-auto px-4">
        <div class="flex flex-col md:flex-row items-center">
          <div class="md:w-1/2 mb-10 md:mb-0 md:pr-12">
            <h2 class="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Comprehensive Risk Assessment</h2>
            <p class="text-xl text-gray-600 mb-6">
              Our advanced risk assessment tools help you identify and mitigate potential supply chain disruptions before they impact your business.
            </p>
            <ul class="space-y-4">
              <li class="flex items-start">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                  <i class="fas fa-check text-blue-600 text-sm"></i>
                </div>
                <p class="text-gray-700">Financial stability analysis of suppliers</p>
              </li>
              <li class="flex items-start">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                  <i class="fas fa-check text-blue-600 text-sm"></i>
                </div>
                <p class="text-gray-700">Geopolitical risk monitoring and alerts</p>
              </li>
              <li class="flex items-start">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                  <i class="fas fa-check text-blue-600 text-sm"></i>
                </div>
                <p class="text-gray-700">Environmental and sustainability compliance tracking</p>
              </li>
              <li class="flex items-start">
                <div class="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center mr-3 mt-1">
                  <i class="fas fa-check text-blue-600 text-sm"></i>
                </div>
                <p class="text-gray-700">Supply chain disruption prediction</p>
              </li>
            </ul>
          </div>
          <div class="md:w-1/2">
            <div class="bg-gray-100 rounded-xl p-6 shadow-lg">
              <img src="https://images.unsplash.com/photo-1512758017271-d7b84c2113f1?auto=format&fit=crop&q=80&w=1000" 
                   alt="Risk Assessment Dashboard showing supplier risk metrics and analysis" class="rounded-lg w-full">
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonials Section -->
    <section class="py-16 bg-gray-50">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">What Our Clients Say</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Hear from supply chain leaders who have transformed their supplier management with Supplier IQ.
          </p>
        </div>

        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          @for (testimonial of testimonials; track testimonial.author) {
            <div class="bg-white rounded-xl p-8 shadow-lg border border-gray-100">
              <div class="text-blue-600 text-4xl mb-4">
                <i class="fas fa-quote-left"></i>
              </div>
              <p class="text-gray-700 mb-6 italic">{{ testimonial.quote }}</p>
              <div class="flex items-center">
                <div class="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold mr-4">
                  {{ testimonial.author.charAt(0) }}
                </div>
                <div>
                  <p class="font-bold text-gray-900">{{ testimonial.author }}</p>
                  <p class="text-gray-600 text-sm">{{ testimonial.position }}, {{ testimonial.company }}</p>
                </div>
              </div>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="py-16 bg-white" id="faq">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-3xl md:text-4xl font-bold mb-4 text-gray-900">Frequently Asked Questions</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto">
            Get answers to common questions about our Supplier IQ platform.
          </p>
        </div>

        <div class="max-w-4xl mx-auto">
          @for (faq of faqs; track faq.question) {
            <div class="mb-6 border-b border-gray-200 pb-6">
              <h3 class="text-xl font-bold mb-3 text-gray-900">{{ faq.question }}</h3>
              <p class="text-gray-600">{{ faq.answer }}</p>
            </div>
          }
        </div>
      </div>
    </section>

    <!-- CTA Section -->
    <section class="py-16 bg-gradient-to-br from-blue-900 to-indigo-900 text-white" id="contact">
      <div class="container mx-auto px-4">
        <div class="max-w-4xl mx-auto text-center">
          <h2 class="text-3xl md:text-4xl font-bold mb-6">Ready to Transform Your Supplier Management?</h2>
          <p class="text-xl mb-8">
            Join industry leaders who rely on Supplier IQ to optimize their supply chain and minimize risks.
          </p>
          <div class="bg-white/10 backdrop-blur-sm rounded-xl p-8 shadow-lg">
            <h3 class="text-2xl font-bold mb-6">Request a Demo</h3>
            <form class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label class="block text-sm font-medium mb-2">First Name</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Last Name</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Company Email</label>
                <input type="email" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400">
              </div>
              <div>
                <label class="block text-sm font-medium mb-2">Company</label>
                <input type="text" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400">
              </div>
              <div class="md:col-span-2">
                <label class="block text-sm font-medium mb-2">How can we help you?</label>
                <textarea rows="4" class="w-full px-4 py-3 rounded-lg bg-white/10 border border-white/20 focus:outline-none focus:ring-2 focus:ring-blue-400"></textarea>
              </div>
              <div class="md:col-span-2">
                <button type="submit" class="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-6 rounded-lg transition duration-300">
                  Submit Request
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  `,
  styles: [`
    .bg-pattern {
      background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
    }
  `]
})
export class SupplierIQComponent {
  supplierMetrics: SupplierMetric[] = [
    {
      label: 'Active Suppliers',
      value: '1,234',
      trend: 'up',
      change: '+5.2%'
    },
    {
      label: 'Risk Score',
      value: '92',
      trend: 'stable',
      change: '0%'
    },
    {
      label: 'On-Time Delivery',
      value: '98.5%',
      trend: 'up',
      change: '+1.2%'
    },
    {
      label: 'Cost Savings',
      value: '$2.1M',
      trend: 'up',
      change: '+12.3%'
    }
  ];

  features: Feature[] = [
    {
      icon: 'fa-chart-line',
      title: 'Risk Assessment',
      description: 'Comprehensive supplier risk assessment using AI algorithms that analyze financial stability, compliance history, geopolitical factors, and operational resilience to identify potential vulnerabilities.'
    },
    {
      icon: 'fa-search',
      title: 'Supplier Discovery',
      description: 'Advanced search and matching algorithms to identify optimal suppliers based on your specific requirements, industry standards, and sustainability criteria.'
    },
    {
      icon: 'fa-tachometer-alt',
      title: 'Performance Monitoring',
      description: 'Real-time tracking of supplier performance metrics including delivery times, quality consistency, compliance adherence, and responsiveness to identify trends and address issues proactively.'
    },
    {
      icon: 'fa-handshake',
      title: 'Relationship Management',
      description: 'Tools to strengthen supplier relationships through structured communication channels, collaborative planning features, and performance feedback mechanisms.'
    },
    {
      icon: 'fa-globe',
      title: 'Supply Chain Visibility',
      description: 'End-to-end visibility across your entire supply chain network, with real-time tracking of materials, components, and finished products from source to destination.'
    },
    {
      icon: 'fa-shield-alt',
      title: 'Compliance Assurance',
      description: 'Automated monitoring of supplier compliance with industry regulations, environmental standards, labor practices, and your organization\'s specific requirements.'
    }
  ];

  testimonials: Testimonial[] = [
    {
      quote: "Supplier IQ has revolutionized how we manage our supplier relationships. We've reduced supply chain disruptions by 37% in the first year alone.",
      author: "Robert Thompson",
      position: "Chief Procurement Officer",
      company: "Global Manufacturing Corp"
    },
    {
      quote: "The risk assessment tools have been invaluable in helping us navigate global supply chain challenges. We identified potential disruptions months in advance.",
      author: "Lisa Chen",
      position: "Supply Chain Director",
      company: "Innovative Electronics"
    },
    {
      quote: "What impressed me most was how quickly we were able to implement and see results. Within weeks, we had insights that would have taken months to gather manually.",
      author: "James Wilson",
      position: "VP of Operations",
      company: "Precision Industries"
    }
  ];

  faqs: FAQ[] = [
    {
      question: "How does Supplier IQ help reduce supply chain risks?",
      answer: "Supplier IQ uses AI and machine learning to continuously monitor suppliers for financial, operational, and geopolitical risks. Our platform provides early warning alerts and recommends mitigation strategies to help you avoid disruptions."
    },
    {
      question: "Can Supplier IQ integrate with our existing procurement systems?",
      answer: "Yes, Supplier IQ offers seamless integration with most major ERP and procurement systems, including SAP, Oracle, and Microsoft Dynamics. Our team will work with you to ensure a smooth implementation."
    },
    {
      question: "How long does it take to implement Supplier IQ?",
      answer: "Most clients are up and running within 4-6 weeks. Our implementation team will work with you to configure the platform to your specific needs and integrate with your existing systems."
    },
    {
      question: "Does Supplier IQ support international suppliers?",
      answer: "Yes, Supplier IQ supports global supply chains with data on suppliers from over 190 countries. Our platform includes language translation features and region-specific risk assessments."
    },
    {
      question: "How does Supplier IQ handle supplier data security?",
      answer: "We take data security seriously. Supplier IQ is SOC 2 Type II certified and GDPR compliant. All data is encrypted both in transit and at rest, and we maintain strict access controls."
    }
  ];
} 